/**
 * `DocsLinks` — GVW-158 docs / about / What's new.
 *
 * Per brief §8: external link uses `target="_blank" rel="noopener noreferrer"`
 * with `aria-label`; What's new is an ordered timeline `<ol>` with each entry
 * an `<li>` containing heading + body. Defence-variant disables external
 * links per brief §7 / §16; offline state disables links with helper copy
 * per `gvw.158.offline.body`.
 *
 * Static config per brief §13; in Phase 3 the bridge will ship the entries.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-158/brief.md
 */

import { useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useDefenceVariant } from '@/design/hooks';
import { useLiveRegion } from '@/state/liveRegion';

interface DocLink {
  id: string;
  title: string;
  description: string;
  url: string;
  icon: string;
}

interface WhatsNewEntry {
  date: string;
  title: string;
  body: string;
}

const DOC_LINKS: DocLink[] = [
  { id: 'user-guide', title: 'User Guide', description: 'Complete guide to GeoViewer for geospatial analysis.', url: 'https://docs.geosoft.com/geoviewer/user-guide', icon: 'menu_book' },
  { id: 'api-docs', title: 'API Documentation', description: 'Developer reference for GeoViewer APIs and integrations.', url: 'https://docs.geosoft.com/geoviewer/api', icon: 'code' },
  { id: 'tutorials', title: 'Tutorial Videos', description: 'Step-by-step tutorials for common workflows.', url: 'https://docs.geosoft.com/geoviewer/tutorials', icon: 'play_circle' },
  { id: 'faq', title: 'FAQ', description: 'Frequently asked questions and troubleshooting.', url: 'https://docs.geosoft.com/geoviewer/faq', icon: 'help' },
  { id: 'requirements', title: 'System Requirements', description: 'Browser compatibility and hardware requirements.', url: 'https://docs.geosoft.com/geoviewer/requirements', icon: 'devices' },
  { id: 'formats', title: 'Data Formats', description: 'Supported file formats and import specifications.', url: 'https://docs.geosoft.com/geoviewer/formats', icon: 'folder' },
];

const WHATS_NEW: WhatsNewEntry[] = [
  { date: '2026-04-27', title: 'Phase 2 — Settings + Help', body: 'Settings landing, share-link defaults, credentials health, integrations mirror, accessibility preferences, telemetry consent, notifications. New keyboard shortcuts reference, version page, system status, and documentation links.' },
  { date: '2026-04-20', title: 'Layer panel improvements', body: 'Search, batch operations, layer groups, custom styling.' },
  { date: '2026-04-15', title: 'Point cloud enhancements', body: 'Elevation, intensity, classification, deviation color modes; slice-offset controls.' },
  { date: '2026-04-10', title: 'Performance optimizations', body: 'Faster initial load and 3D rendering; lower memory use.' },
];

export function DocsLinks() {
  const liveRegion = useLiveRegion();
  const defenceVariant = useDefenceVariant();
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    setOffline(!navigator.onLine);
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    document.title = 'Documentation · GeoViewer';
    liveRegion.announce('status', 'Documentation and what is new loaded.');
  }, [liveRegion]);

  const linksDisabled = defenceVariant.active || offline;
  const disabledHelper = defenceVariant.active
    ? 'External links are disabled in defence variant; local mirror only.'
    : 'Documentation links are unavailable while offline.';

  return (
    <main
      role="main"
      aria-labelledby="gvw-158-heading"
      data-gvw-id="GVW-158"
      data-route="/workspace/help/docs"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-158-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          description
        </span>
        Documentation
      </h1>

      {linksDisabled ? (
        <div
          role="status"
          id="gvw-158-offline-body"
          style={{
            padding: tokens.space[3],
            marginBottom: tokens.space[4],
            background: tokens.color.surface[1],
            borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.warning}`,
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.sm,
          }}
        >
          {disabledHelper}
        </div>
      ) : null}

      <section style={{ marginBottom: tokens.space[6] }} aria-labelledby="gvw-158-whats-new-heading">
        <h2
          id="gvw-158-whats-new-heading"
          style={{
            fontSize: tokens.font.size.xl,
            fontWeight: tokens.font.weight.semibold,
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            marginBottom: tokens.space[3],
          }}
        >
          <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 22 }}>
            new_releases
          </span>
          What's new
        </h2>

        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
          {WHATS_NEW.map((entry) => (
            <li
              key={entry.date}
              style={{
                padding: tokens.space[4],
                background: tokens.color.surface[1],
                border: tokens.border.thin,
                borderRadius: tokens.radius.md,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: tokens.space[2], marginBottom: tokens.space[2] }}>
                <h3 style={{ margin: 0, fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium }}>
                  {entry.title}
                </h3>
                <time
                  dateTime={entry.date}
                  style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}
                >
                  {new Date(entry.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </time>
              </div>
              <p style={{ margin: 0, color: tokens.color.ink.primary, lineHeight: tokens.font.lineHeight.normal }}>
                {entry.body}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="gvw-158-docs-heading">
        <h2
          id="gvw-158-docs-heading"
          style={{ fontSize: tokens.font.size.xl, fontWeight: tokens.font.weight.semibold, marginBottom: tokens.space[3] }}
        >
          Documentation &amp; support
        </h2>

        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${tokens.layout.popoverSm}px, 1fr))`,
            gap: tokens.space[3],
          }}
        >
          {DOC_LINKS.map((link) => (
            <li key={link.id}>
              <a
                href={linksDisabled ? undefined : link.url}
                target={linksDisabled ? undefined : '_blank'}
                rel={linksDisabled ? undefined : 'noopener noreferrer'}
                aria-label={`Open ${link.title} in new tab`}
                aria-disabled={linksDisabled}
                aria-describedby={linksDisabled ? 'gvw-158-offline-body' : undefined}
                title={linksDisabled ? disabledHelper : undefined}
                onClick={(e) => {
                  if (linksDisabled) {
                    e.preventDefault();
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: tokens.space[3],
                  padding: tokens.space[4],
                  background: tokens.color.surface[1],
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                  color: tokens.color.ink.primary,
                  textDecoration: 'none',
                  opacity: linksDisabled ? 0.6 : 1,
                  cursor: linksDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 24, color: tokens.color.ink.secondary, flexShrink: 0 }}>
                  {link.icon}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, marginBottom: tokens.space[1] }}>
                    {link.title}
                  </div>
                  <div style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary, lineHeight: tokens.font.lineHeight.normal }}>
                    {link.description}
                  </div>
                </div>
                <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.ink.muted, flexShrink: 0 }}>
                  open_in_new
                </span>
              </a>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
